/**
 * MangaCompare — Full comparison view between two manga users.
 * Mirrors the anime Compare tab UI/UX with client-side computation.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAniList } from '../context/AniListContext';
import { useAlert } from '../context/ToastContext';
import { useMangaSource } from '../context/MangaSourceContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { fetchPublicMangaList, addAnimeWithRetry } from '../services/aniListService';
import { getCreativeError, getCreativeUserNotFound } from '../utils/errorMessages';
import {
    saveMangaComparison,
    getSavedMangaComparisons,
    getSavedMangaComparison,
    deleteSavedMangaComparison
} from '../services/apiService';
import SiteLinksModal from './SiteLinksModal';
import './Compare.css';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { useTitlePreference } from '../context/TitlePreferenceContext';

// ============================================================
// SORT OPTIONS
// ============================================================
const SORT_OPTIONS = [
    { key: 'averageScore', label: 'Avg Score' },
    { key: 'name', label: 'Name' },
    { key: 'userAScore', label: "A's Score" },
    { key: 'userBScore', label: "B's Score" }
];

// ============================================================
// HELPER: Get display title
// ============================================================
function getTitle(media, titlePreference) {
    return getDisplayTitle(media, titlePreference);
}

// ============================================================
// HELPER: Format number
// ============================================================
function formatNumber(n) {
    if (n == null) return '—';
    return typeof n === 'number' && !Number.isInteger(n) ? n.toFixed(1) : String(n);
}

// ============================================================
// HELPER: Sort manga list
// ============================================================
function sortMangaList(list, sortKey, sortDir, titlePreference) {
    return [...list].sort((a, b) => {
        let va, vb;
        switch (sortKey) {
            case 'name':
                va = getTitle(a.media, titlePreference).toLowerCase();
                vb = getTitle(b.media, titlePreference).toLowerCase();
                return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
            case 'userAScore':
                va = a.userAScore || 0;
                vb = b.userAScore || 0;
                break;
            case 'userBScore':
                va = a.userBScore || 0;
                vb = b.userBScore || 0;
                break;
            case 'averageScore':
            default:
                va = a.averageScore || a.media?.averageScore || 0;
                vb = b.averageScore || b.media?.averageScore || 0;
                break;
        }
        return sortDir === 'asc' ? va - vb : vb - va;
    });
}

// ============================================================
// MANGA CARD — Discover-style (matches AnimeCard in Compare.js)
// ============================================================
const MangaCard = React.memo(({ manga, userAName, userBName, showScores = true, onClick, onAdd, isAdding, userMangaMap, onRead }) => {
    const coverUrl = manga.media?.coverImage?.extraLarge || manga.media?.coverImage?.large || manga.media?.coverImage?.medium;
    const { titlePreference } = useTitlePreference();
    const title = getTitle(manga.media, titlePreference);
    const anilistUrl = manga.media?.id ? `https://anilist.co/manga/${manga.media.id}` : null;
    const year = manga.media?.startDate?.year;
    const [showAddMenu, setShowAddMenu] = React.useState(false);
    const addRef = React.useRef(null);
    const alreadyInList = userMangaMap?.has(manga.media?.id);

    React.useEffect(() => {
        if (!showAddMenu) return;
        const close = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setShowAddMenu(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showAddMenu]);

    return (
        <div className="compare-anime-card" onClick={onClick}>
            <div className="compare-anime-cover">
                {coverUrl ? <img src={coverUrl} alt="" loading="lazy" /> : <div className="compare-anime-no-cover">?</div>}
            </div>
            <div className="compare-anime-info">
                <div className="compare-anime-title" title={title}>
                    {anilistUrl ? (
                        <a href={anilistUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                            {title}
                        </a>
                    ) : title}
                </div>
                <div className="compare-anime-meta">
                    {year && <span>📅 {year}</span>}
                    {manga.media?.chapters && <span>📖 {manga.media.chapters} ch</span>}
                    {manga.media?.volumes && <span>📕 {manga.media.volumes} vol</span>}
                    {manga.media?.averageScore > 0 && <span>⭐ {(manga.media.averageScore / 10).toFixed(1)}</span>}
                </div>
                {showScores && (manga.userAScore > 0 || manga.userBScore > 0) && (
                    <div className="compare-anime-scores">
                        {manga.userAScore > 0 && <span className="score-a" title={userAName}>{userAName}: {manga.userAScore}</span>}
                        {manga.userBScore > 0 && <span className="score-b" title={userBName}>{userBName}: {manga.userBScore}</span>}
                    </div>
                )}
                {manga.media?.genres?.length > 0 && (
                    <div className="compare-anime-genres">
                        {manga.media.genres.slice(0, 3).map((g, i) => (
                            <span key={i} className="compare-genre-tag">{g}</span>
                        ))}
                    </div>
                )}
                {/* Action buttons row */}
                <div className="compare-card-actions">
                    {onRead && (
                        <button
                            className="compare-card-watch-btn"
                            onClick={(e) => { e.stopPropagation(); onRead(); }}
                            title="Read"
                        >
                            📖
                        </button>
                    )}
                    {onAdd && !alreadyInList && (
                        <div className="compare-card-add-wrapper" ref={addRef}>
                            <button
                                className={`compare-card-add-btn ${isAdding ? 'loading' : ''}`}
                                onClick={(e) => { e.stopPropagation(); if (!isAdding) setShowAddMenu(!showAddMenu); }}
                                title="Add to list"
                                disabled={isAdding}
                            >
                                {isAdding ? '⏳' : '➕'}
                            </button>
                            {showAddMenu && (
                                <div className="compare-card-add-menu">
                                    <button onClick={(e) => { e.stopPropagation(); onAdd('PLANNING'); setShowAddMenu(false); }}>📅 Planning</button>
                                    <button onClick={(e) => { e.stopPropagation(); onAdd('CURRENT'); setShowAddMenu(false); }}>📖 Reading</button>
                                    <button onClick={(e) => { e.stopPropagation(); onAdd('COMPLETED'); setShowAddMenu(false); }}>✅ Completed</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

// ============================================================
// MANGA DETAIL MODAL (matches AnimeDetailModal)
// ============================================================
const MangaDetailModal = ({ manga, onClose, userAName, userBName, onAdd, isAdding, userStatus, onRead }) => {
    const [showAddMenu, setShowAddMenu] = React.useState(false);
    const addRef = React.useRef(null);

    React.useEffect(() => {
        if (!showAddMenu) return;
        const close = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setShowAddMenu(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showAddMenu]);

    if (!manga) return null;
    const media = manga.media || {};
    const { titlePreference } = useTitlePreference();
    const title = getTitle(media, titlePreference);
    const altTitle = media.title?.english && media.title?.romaji && media.title.english !== media.title.romaji
        ? media.title.romaji : null;

    const STATUS_MAP = { 'FINISHED': 'Finished', 'RELEASING': 'Publishing', 'NOT_YET_RELEASED': 'Upcoming', 'CANCELLED': 'Cancelled', 'HIATUS': 'Hiatus' };
    const FORMAT_MAP = { 'MANGA': 'Manga', 'LIGHT_NOVEL': 'Light Novel', 'ONE_SHOT': 'One Shot', 'NOVEL': 'Novel', 'MANHWA': 'Manhwa', 'MANHUA': 'Manhua' };
    const statusLabels = { CURRENT: '📖 Reading', PLANNING: '📅 Planning', COMPLETED: '✅ Completed', PAUSED: '⏸️ Paused', DROPPED: '🗑️ Dropped', REPEATING: '🔄 Rereading' };

    const metaParts = [FORMAT_MAP[media.format] || media.format, media.chapters ? `${media.chapters} ch` : null, media.volumes ? `${media.volumes} vol` : null, STATUS_MAP[media.status] || media.status].filter(Boolean);

    return (
        <div className="anime-modal-overlay" onClick={onClose}>
            <div className="anime-modal compare-detail-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>✕</button>

                <div className="modal-content">
                    <div className="modal-header">
                        {media.coverImage?.extraLarge && (
                            <img src={media.coverImage.extraLarge} alt="" className="modal-cover" />
                        )}
                        <div className="modal-header-info">
                            <h2>
                                <a href={`https://anilist.co/manga/${media.id}`} target="_blank" rel="noopener noreferrer">
                                    {title}
                                </a>
                            </h2>
                            {altTitle && <p className="alt-title">{altTitle}</p>}
                            <p className="anime-meta">{metaParts.join(' • ')}</p>
                            {media.averageScore > 0 && (
                                <div className="score-large">⭐ {(media.averageScore / 10).toFixed(1)}/10</div>
                            )}
                            {onRead && (
                                <button className="modal-watch-btn" onClick={onRead}>
                                    📖 Read
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Add to List / Status section */}
                    {onAdd && (
                        <div className="compare-modal-add-section" ref={addRef}>
                            {userStatus ? (
                                <div className="compare-modal-status-badge">
                                    <span className="compare-modal-status-label">In Your List:</span>
                                    <span className="compare-modal-status-value">{statusLabels[userStatus] || userStatus}</span>
                                </div>
                            ) : (
                                <div className="compare-modal-add-wrapper">
                                    <button
                                        className={`compare-modal-add-btn ${isAdding ? 'loading' : ''} ${showAddMenu ? 'open' : ''}`}
                                        onClick={() => { if (!isAdding) setShowAddMenu(!showAddMenu); }}
                                        disabled={isAdding}
                                    >
                                        <span className="add-to-list-emoji">➕</span>
                                        <span className="add-to-list-label">{isAdding ? 'Adding...' : 'Add to List'}</span>
                                        <span className="add-to-list-arrow">{showAddMenu ? '▲' : '▼'}</span>
                                    </button>
                                    {showAddMenu && (
                                        <div className="compare-modal-add-dropdown">
                                            <button onClick={() => { onAdd('PLANNING'); setShowAddMenu(false); }}>📅 Add to Planning</button>
                                            <button onClick={() => { onAdd('CURRENT'); setShowAddMenu(false); }}>📖 Add to Reading</button>
                                            <button onClick={() => { onAdd('COMPLETED'); setShowAddMenu(false); }}>✅ Add to Completed</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* User scores */}
                    {(manga.userAScore > 0 || manga.userBScore > 0) && (
                        <div className="modal-user-scores">
                            {manga.userAScore > 0 && <span className="modal-score-a">{userAName}: {manga.userAScore}/10</span>}
                            {manga.userBScore > 0 && <span className="modal-score-b">{userBName}: {manga.userBScore}/10</span>}
                        </div>
                    )}

                    {media.description && (
                        <div className="modal-description">
                            <h3>Description</h3>
                            <div dangerouslySetInnerHTML={{ __html: media.description }} />
                        </div>
                    )}

                    {media.genres?.length > 0 && (
                        <div className="modal-genres">
                            <h3>Genres</h3>
                            <div className="genre-list">
                                {media.genres.map((g, i) => <span key={i} className="genre-tag-large">{g}</span>)}
                            </div>
                        </div>
                    )}

                    <div className="modal-actions">
                        <a href={`https://anilist.co/manga/${media.id}`} target="_blank" rel="noopener noreferrer" className="btn-anilist">
                            View on AniList
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// OVERLAY for "View All" manga — Discover-style grid
// ============================================================
const MangaListOverlay = ({ title, mangaList, onClose, userAName, userBName, showScores = true, onAdd, addingManga, userMangaMap, onRead }) => {
    const [sortKey, setSortKey] = useState('averageScore');
    const [sortDir, setSortDir] = useState('desc');
    const [detailManga, setDetailManga] = useState(null);

    const { titlePreference } = useTitlePreference();
    const sorted = useMemo(() => sortMangaList(mangaList, sortKey, sortDir, titlePreference), [mangaList, sortKey, sortDir, titlePreference]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    return (
        <div className="compare-overlay-backdrop" onClick={onClose}>
            <div className="compare-overlay compare-overlay-wide" onClick={e => e.stopPropagation()}>
                <div className="compare-overlay-header">
                    <h3>{title} ({mangaList.length})</h3>
                    <button className="compare-overlay-close" onClick={onClose}>✕</button>
                </div>
                <div className="compare-overlay-sort">
                    {SORT_OPTIONS.map(opt => (
                        <button
                            key={opt.key}
                            className={`sort-btn ${sortKey === opt.key ? 'active' : ''}`}
                            onClick={() => toggleSort(opt.key)}
                        >
                            {opt.label} {sortKey === opt.key && (sortDir === 'asc' ? '↑' : '↓')}
                        </button>
                    ))}
                </div>
                <div className="compare-overlay-grid">
                    {sorted.map((manga, i) => (
                        <MangaCard
                            key={manga.media?.id || i}
                            manga={manga}
                            userAName={userAName}
                            userBName={userBName}
                            showScores={showScores}
                            onClick={() => setDetailManga(manga)}
                            onAdd={onAdd ? (status) => onAdd(manga.media?.id, status) : undefined}
                            isAdding={addingManga?.has(manga.media?.id)}
                            userMangaMap={userMangaMap}
                            onRead={onRead ? () => onRead(getTitle(manga.media, titlePreference)) : undefined}
                        />
                    ))}
                </div>

                {detailManga && (
                    <MangaDetailModal
                        manga={detailManga}
                        onClose={() => setDetailManga(null)}
                        userAName={userAName}
                        userBName={userBName}
                        onAdd={onAdd ? (status) => onAdd(detailManga.media?.id, status) : undefined}
                        isAdding={addingManga?.has(detailManga.media?.id)}
                        userStatus={userMangaMap?.get(detailManga.media?.id)}
                        onRead={onRead ? () => onRead(getTitle(detailManga.media, titlePreference)) : undefined}
                    />
                )}
            </div>
        </div>
    );
};

// ============================================================
// Section wrapper component (matches Compare.js)
// ============================================================
const Section = ({ title, icon, id, children }) => {
    const [collapsed, setCollapsed] = useState(false);

    return (
        <div className={`compare-section ${collapsed ? 'collapsed' : ''}`} id={`section-${id}`}>
            <div className="compare-section-header" onClick={() => setCollapsed(!collapsed)}>
                <h3><span className="section-icon">{icon}</span> {title}</h3>
                <span className="section-chevron">{collapsed ? '▸' : '▾'}</span>
            </div>
            {!collapsed && <div className="compare-section-body">{children}</div>}
        </div>
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const MangaCompare = () => {
    const { username, isConnected, token, refreshData } = useAniList();
    const { mangaUserData } = useMangaSource();
    const { showError } = useAlert();
    const { readLinks } = useLinkSettings();
    const { titlePreference } = useTitlePreference();

    const [searchUsername, setSearchUsername] = useState('');
    const [isComparing, setIsComparing] = useState(false);
    const [error, setError] = useState(null);
    const [comparedUsername, setComparedUsername] = useState('');
    const [comparisonResult, setComparisonResult] = useState(null);
    const searchInputRef = useRef(null);

    // Overlay/modal state
    const [overlayData, setOverlayData] = useState(null);
    const [detailManga, setDetailManga] = useState(null);
    const [groupDetailData, setGroupDetailData] = useState(null);
    const [viewAllGroups, setViewAllGroups] = useState(null);

    // Add-to-list state
    const [addingManga, setAddingManga] = useState(new Set());
    const [userMangaMap, setUserMangaMap] = useState(new Map());

    // Read modal state
    const [showReadModal, setShowReadModal] = useState(false);
    const [selectedMangaForRead, setSelectedMangaForRead] = useState(null);

    // Saved comparisons state
    const [savedMangaComparisons, setSavedMangaComparisons] = useState([]);
    const [showSavedOverlay, setShowSavedOverlay] = useState(false);

    // Build user manga map
    useEffect(() => {
        if (!mangaUserData?.lists) return;
        const m = new Map();
        mangaUserData.lists.forEach(list => {
            list.entries.forEach(entry => m.set(entry.media.id, entry.status));
        });
        setUserMangaMap(m);
    }, [mangaUserData]);

    // Add to list handler
    const handleAddToList = useCallback(async (mediaId, status) => {
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }
        setUserMangaMap(prev => new Map(prev).set(mediaId, status));
        setAddingManga(prev => new Set([...prev, mediaId]));
        try {
            await addAnimeWithRetry(mediaId, status, token);
            if (refreshData) refreshData(true);
            setAddingManga(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
        } catch (err) {
            setUserMangaMap(prev => { const next = new Map(prev); next.delete(mediaId); return next; });
            setAddingManga(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
            showError(`Failed to add to list: ${err.message}`, 'Add Failed');
        }
    }, [token, refreshData, showError]);

    // Run comparison
    const handleCompare = useCallback(async (targetOverride) => {
        const target = (targetOverride || searchUsername).trim();
        if (!target) return;
        if (!username) { setError('Please connect to AniList first'); return; }
        if (target.toLowerCase() === username.toLowerCase()) { setError("You can't compare with yourself!"); return; }

        setIsComparing(true);
        setError(null);
        setComparisonResult(null);

        try {
            const userBData = await fetchPublicMangaList(target);
            if (!userBData || !userBData.lists) {
                throw new Error(`Could not fetch manga list for "${target}"`);
            }

            const userAEntries = mangaUserData?.lists?.flatMap(l => l.entries) || [];
            const userBEntries = userBData.lists.flatMap(l => l.entries) || [];

            const userAIds = new Set(userAEntries.map(e => e.media.id));
            const userBIds = new Set(userBEntries.map(e => e.media.id));

            const commonIds = new Set([...userAIds].filter(id => userBIds.has(id)));
            const onlyBIds = new Set([...userBIds].filter(id => !userAIds.has(id)));

            const userBMap = new Map(userBEntries.map(e => [e.media.id, e]));
            const userAMap = new Map(userAEntries.map(e => [e.media.id, e]));

            // Common manga with scores
            const commonManga = userAEntries
                .filter(e => commonIds.has(e.media.id))
                .map(e => ({
                    media: e.media,
                    userAScore: e.score || 0,
                    userBScore: userBMap.get(e.media.id)?.score || 0,
                    userAProgress: e.progress || 0,
                    userBProgress: userBMap.get(e.media.id)?.progress || 0
                }))
                .sort((a, b) => (b.media.averageScore || 0) - (a.media.averageScore || 0));

            // Only B manga
            const onlyBManga = userBEntries
                .filter(e => onlyBIds.has(e.media.id))
                .map(e => ({ media: e.media, userBScore: e.score || 0 }))
                .sort((a, b) => (b.media.averageScore || 0) - (a.media.averageScore || 0));

            // Compatibility score
            const totalUnion = userAIds.size + userBIds.size - commonIds.size;
            const overlapPercent = totalUnion > 0 ? Math.round((commonIds.size / totalUnion) * 100) : 0;
            let scoreSimilarity = 0;
            const scoredCommon = commonManga.filter(m => m.userAScore > 0 && m.userBScore > 0);
            if (scoredCommon.length > 0) {
                const totalDiff = scoredCommon.reduce((sum, m) => sum + Math.abs(m.userAScore - m.userBScore), 0);
                scoreSimilarity = Math.round(100 - (totalDiff / scoredCommon.length) * 10);
            }
            const compatScore = Math.min(100, Math.max(0, Math.round((overlapPercent * 0.4) + (Math.max(0, scoreSimilarity) * 0.6))));

            // Genre analysis
            const genreCountA = {};
            const genreCountB = {};
            userAEntries.forEach(e => (e.media.genres || []).forEach(g => { genreCountA[g] = (genreCountA[g] || 0) + 1; }));
            userBEntries.forEach(e => (e.media.genres || []).forEach(g => { genreCountB[g] = (genreCountB[g] || 0) + 1; }));
            const allGenres = [...new Set([...Object.keys(genreCountA), ...Object.keys(genreCountB)])];
            const genreComparison = allGenres.map(genre => ({
                genre,
                aCount: genreCountA[genre] || 0,
                bCount: genreCountB[genre] || 0
            })).sort((a, b) => (b.aCount + b.bCount) - (a.aCount + a.bCount));
            const commonGenres = allGenres.filter(g => genreCountA[g] && genreCountB[g]);
            const genreOverlap = allGenres.length > 0 ? Math.round((commonGenres.length / allGenres.length) * 100) : 0;

            // Score distribution
            const scoreDistA = {};
            const scoreDistB = {};
            for (let i = 1; i <= 10; i++) { scoreDistA[i] = 0; scoreDistB[i] = 0; }
            userAEntries.forEach(e => { if (e.score > 0) scoreDistA[e.score] = (scoreDistA[e.score] || 0) + 1; });
            userBEntries.forEach(e => { if (e.score > 0) scoreDistB[e.score] = (scoreDistB[e.score] || 0) + 1; });

            // Scoring insights
            const scoredA = userAEntries.filter(e => e.score > 0);
            const scoredB = userBEntries.filter(e => e.score > 0);
            const avgA = scoredA.length > 0 ? (scoredA.reduce((s, e) => s + e.score, 0) / scoredA.length).toFixed(1) : '—';
            const avgB = scoredB.length > 0 ? (scoredB.reduce((s, e) => s + e.score, 0) / scoredB.length).toFixed(1) : '—';
            const getGenerosity = (avg) => { const n = parseFloat(avg); if (isNaN(n)) return 'Unknown'; if (n >= 8) return 'Generous'; if (n >= 6) return 'Fair'; return 'Harsh'; };

            // Hot takes (score disagreements >= 3)
            const hotTakes = scoredCommon
                .map(m => ({ ...m, scoreDiff: Math.abs(m.userAScore - m.userBScore) }))
                .filter(m => m.scoreDiff >= 3)
                .sort((a, b) => b.scoreDiff - a.scoreDiff);

            // Status distribution
            const statusDistA = {};
            const statusDistB = {};
            userAEntries.forEach(e => { statusDistA[e.status] = (statusDistA[e.status] || 0) + 1; });
            userBEntries.forEach(e => { statusDistB[e.status] = (statusDistB[e.status] || 0) + 1; });

            // ── Franchise / Group comparison (union-find on relations) ──
            const parent = {};
            const find = (x) => { if (parent[x] !== x) parent[x] = find(parent[x]); return parent[x]; };
            const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

            // Init every manga id we know about
            const allEntries = [...userAEntries, ...userBEntries];
            const allMediaById = new Map();
            allEntries.forEach(e => {
                const id = e.media.id;
                if (!parent[id]) parent[id] = id;
                if (!allMediaById.has(id)) allMediaById.set(id, e.media);
                // Union via relations (only MANGA-type relations)
                (e.media.relations?.edges || []).forEach(edge => {
                    const relId = edge.node?.id;
                    if (!relId) return;
                    if (!parent[relId]) parent[relId] = relId;
                    union(id, relId);
                });
            });

            // Build groups: root -> list of manga ids
            const groupsMap = new Map();
            [...userAIds, ...userBIds].forEach(id => {
                const root = find(id);
                if (!groupsMap.has(root)) groupsMap.set(root, []);
                groupsMap.get(root).push(id);
            });

            // Only keep groups with 2+ manga
            const matchedGroups = [];
            const onlyBGroups = [];
            groupsMap.forEach((ids, root) => {
                if (ids.length < 2) return;
                const aIds = ids.filter(id => userAIds.has(id));
                const bIds = ids.filter(id => userBIds.has(id));
                const commonGroupIds = ids.filter(id => userAIds.has(id) && userBIds.has(id));
                // Pick the best cover/title (highest popularity)
                const bestMedia = ids.map(id => allMediaById.get(id)).filter(Boolean)
                    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))[0];
                const name = getTitle(bestMedia, titlePreference) || 'Unknown Franchise';
                const coverImage = bestMedia?.coverImage?.extraLarge || bestMedia?.coverImage?.large || null;

                // Build common/onlyA/onlyB anime lists for group detail
                const makeEntry = (id) => {
                    const media = allMediaById.get(id);
                    return {
                        media,
                        userAScore: userAMap.get(id)?.score || 0,
                        userBScore: userBMap.get(id)?.score || 0
                    };
                };

                if (aIds.length > 0 && bIds.length > 0) {
                    matchedGroups.push({
                        name, coverImage,
                        aAnimeCount: aIds.length,
                        bAnimeCount: bIds.length,
                        commonCount: commonGroupIds.length,
                        totalInGroup: ids.length,
                        commonAnime: commonGroupIds.map(makeEntry),
                        onlyAAnime: aIds.filter(id => !userBIds.has(id)).map(makeEntry),
                        onlyBAnime: bIds.filter(id => !userAIds.has(id)).map(makeEntry)
                    });
                } else if (bIds.length > 0 && aIds.length === 0) {
                    onlyBGroups.push({
                        name, coverImage,
                        animeCount: bIds.length,
                        anime: bIds.map(makeEntry)
                    });
                }
            });
            matchedGroups.sort((a, b) => b.commonCount - a.commonCount);
            onlyBGroups.sort((a, b) => b.animeCount - a.animeCount);

            const totalGroupsA = [...new Set([...userAIds].map(id => find(id)))].length;
            const totalGroupsB = [...new Set([...userBIds].map(id => find(id)))].length;

            // ── Head-to-Head Leaderboard ──
            const h2hCategories = [
                { label: 'Total Manga', icon: '📚', aValue: userAIds.size, bValue: userBIds.size },
                { label: 'Scored Manga', icon: '⭐', aValue: scoredA.length, bValue: scoredB.length },
                { label: 'Average Score', icon: '📊', aValue: parseFloat(avgA) || 0, bValue: parseFloat(avgB) || 0 },
                { label: 'Chapters Read', icon: '📖', aValue: userAEntries.reduce((s, e) => s + (e.progress || 0), 0), bValue: userBEntries.reduce((s, e) => s + (e.progress || 0), 0) },
                { label: 'Franchises', icon: '📦', aValue: totalGroupsA, bValue: totalGroupsB },
                { label: 'Genres Explored', icon: '🎭', aValue: Object.keys(genreCountA).length, bValue: Object.keys(genreCountB).length }
            ].map(c => ({ ...c, winner: c.aValue > c.bValue ? 'A' : c.bValue > c.aValue ? 'B' : 'tie' }));
            const h2hAWins = h2hCategories.filter(c => c.winner === 'A').length;
            const h2hBWins = h2hCategories.filter(c => c.winner === 'B').length;
            const headToHead = {
                categories: h2hCategories,
                aWins: h2hAWins,
                bWins: h2hBWins,
                overallWinner: h2hAWins > h2hBWins ? 'A' : h2hBWins > h2hAWins ? 'B' : 'tie'
            };

            // ── Rating Correlation (Pearson) ──
            let ratingCorrelation = null;
            const coScored = scoredCommon.filter(m => m.userAScore > 0 && m.userBScore > 0);
            if (coScored.length >= 5) {
                const n = coScored.length;
                const sumA = coScored.reduce((s, m) => s + m.userAScore, 0);
                const sumB = coScored.reduce((s, m) => s + m.userBScore, 0);
                const sumAB = coScored.reduce((s, m) => s + m.userAScore * m.userBScore, 0);
                const sumA2 = coScored.reduce((s, m) => s + m.userAScore * m.userAScore, 0);
                const sumB2 = coScored.reduce((s, m) => s + m.userBScore * m.userBScore, 0);
                const denom = Math.sqrt((n * sumA2 - sumA * sumA) * (n * sumB2 - sumB * sumB));
                const corr = denom > 0 ? ((n * sumAB - sumA * sumB) / denom) : 0;
                ratingCorrelation = { correlation: parseFloat(corr.toFixed(2)), sampleSize: n };
            }

            // ── Temporal / Era Analysis ──
            const decadeCountA = {};
            const decadeCountB = {};
            userAEntries.forEach(e => {
                const yr = e.media?.startDate?.year;
                if (yr) { const dec = `${Math.floor(yr / 10) * 10}s`; decadeCountA[dec] = (decadeCountA[dec] || 0) + 1; }
            });
            userBEntries.forEach(e => {
                const yr = e.media?.startDate?.year;
                if (yr) { const dec = `${Math.floor(yr / 10) * 10}s`; decadeCountB[dec] = (decadeCountB[dec] || 0) + 1; }
            });
            const allDecades = [...new Set([...Object.keys(decadeCountA), ...Object.keys(decadeCountB)])].sort();
            const decades = allDecades.map(d => ({ decade: d, aCount: decadeCountA[d] || 0, bCount: decadeCountB[d] || 0 }));

            setComparisonResult({
                compatScore,
                commonCount: commonIds.size,
                onlyACount: userAIds.size - commonIds.size,
                onlyBCount: onlyBIds.size,
                userATotal: userAIds.size,
                userBTotal: userBIds.size,
                commonPercentOfA: userAIds.size > 0 ? Math.round((commonIds.size / userAIds.size) * 100) : 0,
                commonPercentOfB: userBIds.size > 0 ? Math.round((commonIds.size / userBIds.size) * 100) : 0,
                commonManga,
                onlyBManga,
                userBAvatar: userBData.user?.avatar?.large || null,
                genreAnalysis: { genreComparison: genreComparison.slice(0, 12), overlapScore: genreOverlap },
                scoreDistribution: { userA: scoreDistA, userB: scoreDistB },
                scoringInsights: {
                    userA: { avgScore: avgA, generosity: getGenerosity(avgA), scoredCount: scoredA.length, unscored: userAEntries.length - scoredA.length },
                    userB: { avgScore: avgB, generosity: getGenerosity(avgB), scoredCount: scoredB.length, unscored: userBEntries.length - scoredB.length }
                },
                hotTakes,
                statusDistribution: { userA: statusDistA, userB: statusDistB },
                headToHead,
                ratingCorrelation,
                temporalAnalysis: { decades },
                groupComparison: {
                    matchedGroups,
                    onlyBGroups,
                    matchedCount: matchedGroups.length,
                    totalGroupsA,
                    totalGroupsB,
                    onlyBGroupCount: onlyBGroups.length
                }
            });
            setComparedUsername(target);

            // Auto-save comparison to server (fire-and-forget)
            saveMangaComparison(
                username,
                target,
                userBData.user?.avatar?.large || null,
                {
                    compatScore,
                    commonCount: commonIds.size,
                    userATotal: userAIds.size,
                    userBTotal: userBIds.size,
                }
            ).then(() => {
                loadSavedMangaComparisons();
            }).catch(err => console.warn('Failed to save manga comparison:', err));
        } catch (err) {
            console.error('Manga compare failed:', err);
            const status = err.response?.status || err?.status;
            if (status === 404 || err.message?.includes('not found') || err.message?.includes('Not Found')) {
                setError(getCreativeUserNotFound(target));
            } else if (err.message?.includes('private') || err.message?.includes('Private')) {
                setError(`🔒 "${target}" has a private profile — can't peek at their manga list!`);
            } else {
                setError(getCreativeError(err.message || 'Comparison failed'));
            }
        } finally {
            setIsComparing(false);
        }
    }, [searchUsername, username, mangaUserData, titlePreference]);

    // Load saved manga comparisons from server
    const loadSavedMangaComparisons = useCallback(async () => {
        if (!username) return;
        try {
            const result = await getSavedMangaComparisons(username);
            setSavedMangaComparisons(result?.comparisons || []);
        } catch (err) {
            console.warn('Failed to load saved manga comparisons:', err);
        }
    }, [username]);

    // Load saved comparisons on mount
    useEffect(() => {
        if (isConnected && username) {
            loadSavedMangaComparisons();
        }
    }, [isConnected, username, loadSavedMangaComparisons]);

    // Load a saved manga comparison — runs a fresh comparison with that username
    const handleLoadSaved = useCallback(async (userBUsername) => {
        // Bump the saved comparison's timestamp so it stays most-recent
        getSavedMangaComparison(username, userBUsername).catch(() => { });
        loadSavedMangaComparisons();
        // Set the search field and trigger a fresh comparison
        setSearchUsername(userBUsername);
        handleCompare(userBUsername);
    }, [username, loadSavedMangaComparisons, handleCompare]);

    // Delete a saved manga comparison
    const handleDeleteSaved = useCallback(async (userBUsername, e) => {
        if (e) e.stopPropagation();
        try {
            await deleteSavedMangaComparison(username, userBUsername);
            loadSavedMangaComparisons();
            // If we deleted the currently displayed comparison, clear it
            if (comparedUsername === userBUsername) {
                setComparisonResult(null);
                setComparedUsername('');
            }
        } catch (err) {
            console.warn('Failed to delete manga comparison:', err);
        }
    }, [username, comparedUsername, loadSavedMangaComparisons]);

    // Saved comparisons helpers
    const mostRecentSaved = savedMangaComparisons.length > 0 ? savedMangaComparisons[0] : null;
    const hasMoreSaved = savedMangaComparisons.length > 1;

    const handleKeyDown = (e) => { if (e.key === 'Enter') handleCompare(); };

    // Compatibility label
    const getLabel = (s) => {
        if (s >= 90) return 'Manga Soulmates — You share one library! 💞';
        if (s >= 75) return 'Kindred Readers — Remarkably similar taste! 🔥';
        if (s >= 60) return 'Reading Buddies — Lots in common! 🤝';
        if (s >= 45) return 'Crossing Paths — Some shared favorites 🌟';
        if (s >= 30) return 'Parallel Worlds — Different but interesting 🌈';
        if (s >= 15) return 'Different Shelves — Rarely the same picks 📚';
        return 'Multiverse Split — Completely different taste! ⚡';
    };

    // ============================================================
    // RENDER SECTIONS
    // ============================================================
    const result = comparisonResult;
    const previewCount = 6;

    const renderCompatibilityHero = () => {
        if (!result) return null;
        const score = result.compatScore;
        const hue = Math.round((score / 100) * 120);
        return (
            <div className="compare-hero" style={{ '--compat-hue': hue }}>
                <div className="compare-hero-score">
                    <div className="hero-ring" style={{ '--progress': `${score}%` }}>
                        <span className="hero-number">{score}%</span>
                    </div>
                </div>
                <div className="compare-hero-label">{getLabel(score)}</div>
                <div className="compare-hero-subtitle">
                    Manga compatibility between <strong>{username}</strong> and <strong>{comparedUsername}</strong>
                </div>
            </div>
        );
    };

    const renderGroupComparison = () => {
        if (!result?.groupComparison) return null;
        const gc = result.groupComparison;
        if ((!gc.matchedGroups || gc.matchedGroups.length === 0) && (!gc.onlyBGroups || gc.onlyBGroups.length === 0)) return null;

        const groupPreviewCount = 6;
        const visibleGroups = (gc.matchedGroups || []).slice(0, groupPreviewCount);
        const hasMoreGroups = (gc.matchedGroups || []).length > groupPreviewCount;

        const onlyBGroups = gc.onlyBGroups || [];
        const visibleOnlyBGroups = onlyBGroups.slice(0, groupPreviewCount);

        const renderGroupCard = (group, i) => {
            const total = group.totalInGroup || (group.aAnimeCount + group.bAnimeCount - group.commonCount);
            const completionA = total > 0 ? Math.round((group.aAnimeCount / total) * 100) : 0;
            const completionB = total > 0 ? Math.round((group.bAnimeCount / total) * 100) : 0;

            return (
                <div key={i} className="group-card" onClick={() => setGroupDetailData(group)}>
                    <div className="group-card-cover">
                        {group.coverImage ? (
                            <img src={group.coverImage} alt="" loading="lazy" />
                        ) : (
                            <div className="group-card-no-cover">📦</div>
                        )}
                    </div>
                    <div className="group-card-info">
                        <div className="group-card-name" title={group.name}>{group.name}</div>
                        <div className="group-card-counts">
                            <span className="gcc-common">🤝 {group.commonCount} shared</span>
                            <span className="gcc-total">{total} manga total</span>
                        </div>
                        <div className="group-card-bars">
                            <div className="group-bar-row">
                                <span className="gbr-label gbr-a">{username}</span>
                                <div className="gbr-track">
                                    <div className="gbr-fill gbr-fill-a" style={{ width: `${completionA}%` }} />
                                </div>
                                <span className="gbr-count">{group.aAnimeCount}/{total}</span>
                            </div>
                            <div className="group-bar-row">
                                <span className="gbr-label gbr-b">{comparedUsername}</span>
                                <div className="gbr-track">
                                    <div className="gbr-fill gbr-fill-b" style={{ width: `${completionB}%` }} />
                                </div>
                                <span className="gbr-count">{group.bAnimeCount}/{total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        const renderOnlyBGroupCard = (group, i) => {
            const handleClick = () => {
                setGroupDetailData({
                    name: group.name,
                    coverImage: group.coverImage,
                    commonCount: 0,
                    aAnimeCount: 0,
                    bAnimeCount: group.animeCount,
                    totalInGroup: group.animeCount,
                    commonAnime: [],
                    onlyAAnime: [],
                    onlyBAnime: group.anime || []
                });
            };

            return (
                <div key={`onlyb-${i}`} className="group-card" onClick={handleClick}>
                    <div className="group-card-cover">
                        {group.coverImage ? (
                            <img src={group.coverImage} alt="" loading="lazy" />
                        ) : (
                            <div className="group-card-no-cover">📦</div>
                        )}
                    </div>
                    <div className="group-card-info">
                        <div className="group-card-name" title={group.name}>{group.name}</div>
                        <div className="group-card-counts">
                            <span className="gcc-total">📖 {group.animeCount} manga</span>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <Section title="Franchise Comparison" icon="📦" id="groups">
                <div className="group-summary-bar">
                    <div className="gsb-item gsb-a">
                        <span className="gsb-count">{gc.totalGroupsA}</span>
                        <span className="gsb-label">{username}'s franchises</span>
                    </div>
                    <div className="gsb-item gsb-match">
                        <span className="gsb-count">{gc.matchedCount}</span>
                        <span className="gsb-label">Shared</span>
                    </div>
                    <div className="gsb-item gsb-b">
                        <span className="gsb-count">{gc.totalGroupsB}</span>
                        <span className="gsb-label">{comparedUsername}'s franchises</span>
                    </div>
                </div>
                {gc.onlyBGroupCount > 0 && (
                    <div className="group-unique-note">
                        <span className="gun-b">{gc.onlyBGroupCount} only in {comparedUsername}'s list</span>
                    </div>
                )}

                {/* Common Franchises */}
                {visibleGroups.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Common Franchises</h4>
                            {hasMoreGroups && (
                                <button className="view-all-btn" onClick={() => setViewAllGroups(gc.matchedGroups)}>
                                    View All ({gc.matchedGroups.length})
                                </button>
                            )}
                        </div>
                        <div className="group-cards-grid">
                            {visibleGroups.map(renderGroupCard)}
                        </div>
                    </div>
                )}

                {/* Only User B Has */}
                {visibleOnlyBGroups.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Only {comparedUsername} Has</h4>
                        </div>
                        <div className="group-cards-grid">
                            {visibleOnlyBGroups.map(renderOnlyBGroupCard)}
                        </div>
                    </div>
                )}
            </Section>
        );
    };

    const renderMangaComparison = () => {
        if (!result) return null;
        const commonPreview = sortMangaList(result.commonManga || [], 'averageScore', 'desc').slice(0, previewCount);
        const onlyBPreview = sortMangaList(result.onlyBManga || [], 'averageScore', 'desc').slice(0, previewCount);

        return (
            <Section title="Manga Comparison" icon="📊" id="mangacompare">
                <div className="anime-compare-summary">
                    <div className="acs-stat">
                        <span className="acs-count">{result.userATotal}</span>
                        <span className="acs-label">{username}'s manga</span>
                    </div>
                    <div className="acs-stat acs-common">
                        <span className="acs-count">{result.commonCount}</span>
                        <span className="acs-label">Common</span>
                        <span className="acs-percent">
                            <span className="acs-pct-a">{result.commonPercentOfA}% of {username}</span>
                            <span className="acs-pct-sep">·</span>
                            <span className="acs-pct-b">{result.commonPercentOfB}% of {comparedUsername}</span>
                        </span>
                    </div>
                    <div className="acs-stat">
                        <span className="acs-count">{result.userBTotal}</span>
                        <span className="acs-label">{comparedUsername}'s manga</span>
                    </div>
                </div>

                {commonPreview.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Common Manga</h4>
                            {(result.commonManga?.length || 0) > previewCount && (
                                <button className="view-all-btn" onClick={() => setOverlayData({
                                    title: 'Common Manga',
                                    list: result.commonManga,
                                    showScores: true
                                })}>View All ({result.commonManga.length})</button>
                            )}
                        </div>
                        <div className="compare-anime-grid">
                            {commonPreview.map((manga, i) => (
                                <MangaCard key={manga.media?.id || i} manga={manga} userAName={username} userBName={comparedUsername} onClick={() => setDetailManga(manga)} onAdd={(status) => handleAddToList(manga.media?.id, status)} isAdding={addingManga.has(manga.media?.id)} userMangaMap={userMangaMap} onRead={() => { setSelectedMangaForRead(getTitle(manga.media, titlePreference)); setShowReadModal(true); }} />
                            ))}
                        </div>
                    </div>
                )}

                {onlyBPreview.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Only {comparedUsername} Has</h4>
                            {(result.onlyBManga?.length || 0) > previewCount && (
                                <button className="view-all-btn" onClick={() => setOverlayData({
                                    title: `Only ${comparedUsername} Has`,
                                    list: result.onlyBManga,
                                    showScores: false
                                })}>View All ({result.onlyBManga.length})</button>
                            )}
                        </div>
                        <div className="compare-anime-grid">
                            {onlyBPreview.map((manga, i) => (
                                <MangaCard key={manga.media?.id || i} manga={manga} userAName={username} userBName={comparedUsername} showScores={false} onClick={() => setDetailManga(manga)} onAdd={(status) => handleAddToList(manga.media?.id, status)} isAdding={addingManga.has(manga.media?.id)} userMangaMap={userMangaMap} onRead={() => { setSelectedMangaForRead(getTitle(manga.media, titlePreference)); setShowReadModal(true); }} />
                            ))}
                        </div>
                    </div>
                )}
            </Section>
        );
    };

    const renderGenreAnalysis = () => {
        if (!result?.genreAnalysis) return null;
        const ga = result.genreAnalysis;
        const topGenres = ga.genreComparison;

        return (
            <Section title="Genre Compatibility" icon="🎭" id="genre">
                <div className="genre-overlap-score">
                    <span className="genre-score-value">{ga.overlapScore}%</span>
                    <span className="genre-score-label">Genre Overlap</span>
                </div>
                <div className="genre-bars">
                    {topGenres.map((g, i) => {
                        const maxCount = Math.max(g.aCount, g.bCount, 1);
                        return (
                            <div key={i} className="genre-bar-row">
                                <div className="genre-bar-label">{g.genre}</div>
                                <div className="genre-bar-track">
                                    <div className="genre-bar-a" style={{ width: `${(g.aCount / maxCount) * 100}%` }}>
                                        <span>{g.aCount}</span>
                                    </div>
                                    <div className="genre-bar-b" style={{ width: `${(g.bCount / maxCount) * 100}%` }}>
                                        <span>{g.bCount}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>
        );
    };

    const renderScoreDistribution = () => {
        if (!result?.scoreDistribution) return null;
        const sd = result.scoreDistribution;
        const maxCount = Math.max(...Object.values(sd.userA), ...Object.values(sd.userB), 1);

        return (
            <Section title="Score Distribution" icon="📈" id="scores">
                <div className="score-dist-chart">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                        <div key={score} className="score-dist-column">
                            <div className="score-dist-bars">
                                <div className="sdb-a" style={{ height: `${(sd.userA[score] / maxCount) * 100}%` }} title={`${username}: ${sd.userA[score]}`}>
                                    {sd.userA[score] > 0 && <span>{sd.userA[score]}</span>}
                                </div>
                                <div className="sdb-b" style={{ height: `${(sd.userB[score] / maxCount) * 100}%` }} title={`${comparedUsername}: ${sd.userB[score]}`}>
                                    {sd.userB[score] > 0 && <span>{sd.userB[score]}</span>}
                                </div>
                            </div>
                            <div className="score-dist-label">{score}</div>
                        </div>
                    ))}
                </div>
                <div className="score-dist-legend">
                    <span className="sdl-a">■ {username}</span>
                    <span className="sdl-b">■ {comparedUsername}</span>
                </div>
            </Section>
        );
    };

    const renderScoringInsights = () => {
        if (!result?.scoringInsights) return null;
        const si = result.scoringInsights;

        return (
            <Section title="Scoring Insights" icon="🎯" id="insights">
                <div className="scoring-cards">
                    <div className="scoring-card">
                        <div className="sc-name">{username}</div>
                        <div className="sc-avg">{si.userA.avgScore}</div>
                        <div className="sc-label">Avg Score</div>
                        <div className={`sc-style sc-${si.userA.generosity.toLowerCase()}`}>{si.userA.generosity}</div>
                        <div className="sc-detail">{si.userA.scoredCount} scored · {si.userA.unscored} unscored</div>
                    </div>
                    <div className="scoring-vs">VS</div>
                    <div className="scoring-card">
                        <div className="sc-name">{comparedUsername}</div>
                        <div className="sc-avg">{si.userB.avgScore}</div>
                        <div className="sc-label">Avg Score</div>
                        <div className={`sc-style sc-${si.userB.generosity.toLowerCase()}`}>{si.userB.generosity}</div>
                        <div className="sc-detail">{si.userB.scoredCount} scored · {si.userB.unscored} unscored</div>
                    </div>
                </div>
            </Section>
        );
    };

    const renderStatusDistribution = () => {
        if (!result?.statusDistribution) return null;
        const sd = result.statusDistribution;
        const statuses = ['COMPLETED', 'CURRENT', 'PLANNING', 'PAUSED', 'DROPPED'];
        const labels = { COMPLETED: 'Completed', CURRENT: 'Reading', PLANNING: 'Planning', PAUSED: 'Paused', DROPPED: 'Dropped' };
        const icons = { COMPLETED: '✅', CURRENT: '📖', PLANNING: '📋', PAUSED: '⏸️', DROPPED: '❌' };
        const totalA = Object.values(sd.userA).reduce((s, v) => s + v, 0) || 1;
        const totalB = Object.values(sd.userB).reduce((s, v) => s + v, 0) || 1;

        return (
            <Section title="Status Distribution" icon="📊" id="status">
                <div className="status-comparison">
                    {statuses.map(status => {
                        const aVal = sd.userA[status] || 0;
                        const bVal = sd.userB[status] || 0;
                        const aPct = Math.round((aVal / totalA) * 100);
                        const bPct = Math.round((bVal / totalB) * 100);
                        const total = aVal + bVal;
                        const aWidth = total > 0 ? (aVal / total) * 100 : 50;
                        const bWidth = total > 0 ? (bVal / total) * 100 : 50;
                        return (
                            <div key={status} className="status-row">
                                <div className="status-label">{icons[status]} {labels[status]}</div>
                                <div className="status-bar-track">
                                    {aVal > 0 && <div className="status-bar-a" style={{ width: `${aWidth}%` }}><span>{aVal}</span></div>}
                                    {bVal > 0 && <div className="status-bar-b" style={{ width: `${bWidth}%` }}><span>{bVal}</span></div>}
                                </div>
                                <div className="status-values">
                                    <span className="sv-a">{username}: {aPct}%</span>
                                    <span className="sv-b">{comparedUsername}: {bPct}%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>
        );
    };

    const renderHotTakes = () => {
        if (!result?.hotTakes?.length) return null;

        return (
            <Section title="Hot Takes 🔥" icon="🌶️" id="hottakes">
                <p className="section-subtitle">Biggest score disagreements (≥3 points)</p>
                <div className="hot-takes-grid">
                    {result.hotTakes.slice(0, 8).map((manga, i) => (
                        <div key={manga.media?.id || i} className="hot-take-card" onClick={() => setDetailManga(manga)}>
                            <div className="htc-cover">
                                {manga.media?.coverImage?.large ? (
                                    <img src={manga.media.coverImage.extraLarge || manga.media.coverImage.large} alt="" loading="lazy" />
                                ) : <div className="htc-no-cover">?</div>}
                            </div>
                            <div className="htc-info">
                                <div className="htc-title">{getTitle(manga.media, titlePreference)}</div>
                                <div className="htc-scores">
                                    <span className="htc-a">{username}: {manga.userAScore}</span>
                                    <span className="htc-b">{comparedUsername}: {manga.userBScore}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        );
    };

    const renderHeadToHead = () => {
        if (!result?.headToHead) return null;
        const h2h = result.headToHead;

        return (
            <Section title="Head-to-Head Leaderboard" icon="🏆" id="headtohead">
                <div className="h2h-grid">
                    {h2h.categories.map((cat, i) => (
                        <div key={i} className={`h2h-row ${cat.winner === 'A' ? 'a-wins' : cat.winner === 'B' ? 'b-wins' : 'tie'}`}>
                            <div className="h2h-a-value">{formatNumber(cat.aValue)}</div>
                            <div className="h2h-label">
                                <span className="h2h-icon">{cat.icon}</span>
                                <span>{cat.label}</span>
                            </div>
                            <div className="h2h-b-value">{formatNumber(cat.bValue)}</div>
                        </div>
                    ))}
                </div>
                <div className="h2h-summary">
                    {h2h.overallWinner === 'A' ? `${username} leads ${h2h.aWins}-${h2h.bWins}` :
                        h2h.overallWinner === 'B' ? `${comparedUsername} leads ${h2h.bWins}-${h2h.aWins}` :
                            `It's a tie ${h2h.aWins}-${h2h.bWins}`}
                </div>
            </Section>
        );
    };

    const renderRatingCorrelation = () => {
        if (!result?.ratingCorrelation) return null;
        const rc = result.ratingCorrelation;

        return (
            <Section title="Rating Correlation" icon="📐" id="correlation">
                <div className="correlation-display">
                    <div className="corr-value" style={{ color: rc.correlation > 0.5 ? 'var(--green)' : rc.correlation > 0 ? 'var(--yellow)' : 'var(--red)' }}>
                        {rc.correlation > 0 ? '+' : ''}{rc.correlation}
                    </div>
                    <div className="corr-label">
                        Pearson correlation ({rc.sampleSize} manga with mutual scores)
                    </div>
                </div>
            </Section>
        );
    };

    const renderTemporalAnalysis = () => {
        if (!result?.temporalAnalysis?.decades?.length) return null;
        const decades = result.temporalAnalysis.decades;
        const maxCount = Math.max(...decades.flatMap(d => [d.aCount, d.bCount]), 1);

        return (
            <Section title="Timeline / Era Comparison" icon="📅" id="timeline">
                <div className="timeline-bars">
                    {decades.map((d, i) => (
                        <div key={i} className="timeline-row">
                            <div className="timeline-decade">{d.decade}</div>
                            <div className="timeline-bar-track">
                                {d.aCount > 0 && (
                                    <div className="timeline-bar-a" style={{ width: `${(d.aCount / maxCount) * 100}%` }}>
                                        <span>{d.aCount}</span>
                                    </div>
                                )}
                                {d.bCount > 0 && (
                                    <div className="timeline-bar-b" style={{ width: `${(d.bCount / maxCount) * 100}%` }}>
                                        <span>{d.bCount}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        );
    };

    // ============================================================
    // NOT CONNECTED
    // ============================================================
    if (!isConnected) {
        return (
            <div className="compare-container">
                <div className="compare-empty-state">
                    <div className="compare-empty-icon">🔗</div>
                    <h2>Connect to AniList</h2>
                    <p>Connect your AniList account to compare manga lists with other users.</p>
                </div>
            </div>
        );
    }

    // ============================================================
    // MAIN RENDER
    // ============================================================
    return (
        <div className="compare-container">
            {/* Search + Saved */}
            <div className="compare-top">
                <div className="compare-search-section">
                    <h2>Compare Manga Lists</h2>
                    <div className="compare-search-row">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Enter AniList username..."
                            value={searchUsername}
                            onChange={e => setSearchUsername(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isComparing}
                            className="compare-search-input"
                        />
                        <button
                            onClick={handleCompare}
                            disabled={isComparing || !searchUsername.trim()}
                            className="compare-search-btn"
                        >
                            {isComparing ? '⏳ Comparing...' : '⚔️ Compare'}
                        </button>
                    </div>
                    {error && <div className="compare-error">{error}</div>}
                </div>

                {/* Most recent saved comparison */}
                {mostRecentSaved && (
                    <div className="compare-saved-section">
                        <h3>Recent Comparison</h3>
                        <div
                            className={`saved-item ${comparedUsername === mostRecentSaved.userBUsername ? 'active' : ''}`}
                            onClick={() => handleLoadSaved(mostRecentSaved.userBUsername)}
                        >
                            <div className="saved-avatar">
                                {mostRecentSaved.userBAvatar ? (
                                    <img src={mostRecentSaved.userBAvatar} alt="" />
                                ) : (
                                    <div className="saved-avatar-placeholder">{mostRecentSaved.userBUsername[0]?.toUpperCase()}</div>
                                )}
                            </div>
                            <div className="saved-info">
                                <div className="saved-name">{mostRecentSaved.userBUsername}</div>
                                <div className="saved-meta">
                                    {mostRecentSaved.summary?.compatScore}% compat · {mostRecentSaved.summary?.commonCount} common
                                </div>
                            </div>
                            <button className="saved-delete" onClick={(e) => handleDeleteSaved(mostRecentSaved.userBUsername, e)} title="Delete">✕</button>
                        </div>
                        {hasMoreSaved && (
                            <button className="view-all-saved-btn" onClick={() => setShowSavedOverlay(true)}>
                                View All ({savedMangaComparisons.length})
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Loading */}
            {isComparing && (
                <div className="compare-loading">
                    <div className="compare-loading-spinner" />
                    <p>Comparing manga lists...</p>
                </div>
            )}

            {/* Results */}
            {result && !isComparing && (
                <div className="compare-results">
                    <div className="compare-action-bar">
                        <span className="cab-label">Comparing with <strong>{comparedUsername}</strong></span>
                        <div className="cab-actions">
                            <button className="cab-btn" onClick={handleCompare} title="Refresh comparison">🔄 Refresh</button>
                            <button className="cab-btn cab-new" onClick={() => { setComparisonResult(null); setComparedUsername(''); searchInputRef.current?.focus(); }}>➕ New</button>
                        </div>
                    </div>

                    {renderCompatibilityHero()}
                    {renderGroupComparison()}
                    {renderMangaComparison()}
                    {renderHeadToHead()}
                    {renderGenreAnalysis()}
                    {renderScoreDistribution()}
                    {renderScoringInsights()}
                    {renderRatingCorrelation()}
                    {renderTemporalAnalysis()}
                    {renderStatusDistribution()}
                    {renderHotTakes()}
                </div>
            )}

            {/* Empty state */}
            {!result && !isComparing && (
                <div className="compare-empty-state">
                    <div className="compare-empty-icon">⚔️</div>
                    <h2>Ready to Compare</h2>
                    <p>Enter an AniList username above to compare your manga taste.</p>
                </div>
            )}

            {/* View All overlay */}
            {overlayData && (
                <MangaListOverlay
                    title={overlayData.title}
                    mangaList={overlayData.list}
                    onClose={() => setOverlayData(null)}
                    userAName={username}
                    userBName={comparedUsername}
                    showScores={overlayData.showScores}
                    onAdd={handleAddToList}
                    addingManga={addingManga}
                    userMangaMap={userMangaMap}
                    onRead={(mangaName) => { setSelectedMangaForRead(mangaName); setShowReadModal(true); }}
                />
            )}

            {/* View All Franchises overlay */}
            {viewAllGroups && (
                <div className="compare-overlay-backdrop" onClick={() => setViewAllGroups(null)}>
                    <div className="compare-overlay compare-overlay-wide" onClick={e => e.stopPropagation()}>
                        <div className="compare-overlay-header">
                            <h3>📦 All Shared Franchises ({viewAllGroups.length})</h3>
                            <button className="compare-overlay-close" onClick={() => setViewAllGroups(null)}>✕</button>
                        </div>
                        <div className="group-detail-content">
                            <div className="group-cards-grid">
                                {viewAllGroups.map((group, i) => {
                                    const total = group.totalInGroup || (group.aAnimeCount + group.bAnimeCount - group.commonCount);
                                    const completionA = total > 0 ? Math.round((group.aAnimeCount / total) * 100) : 0;
                                    const completionB = total > 0 ? Math.round((group.bAnimeCount / total) * 100) : 0;
                                    return (
                                        <div key={i} className="group-card" onClick={() => setGroupDetailData(group)}>
                                            <div className="group-card-cover">
                                                {group.coverImage ? <img src={group.coverImage} alt="" loading="lazy" /> : <div className="group-card-no-cover">📦</div>}
                                            </div>
                                            <div className="group-card-info">
                                                <div className="group-card-name" title={group.name}>{group.name}</div>
                                                <div className="group-card-counts">
                                                    <span className="gcc-common">🤝 {group.commonCount} shared</span>
                                                    <span className="gcc-total">{total} manga total</span>
                                                </div>
                                                <div className="group-card-bars">
                                                    <div className="group-bar-row">
                                                        <span className="gbr-label gbr-a">{username}</span>
                                                        <div className="gbr-track"><div className="gbr-fill gbr-fill-a" style={{ width: `${completionA}%` }} /></div>
                                                        <span className="gbr-count">{group.aAnimeCount}/{total}</span>
                                                    </div>
                                                    <div className="group-bar-row">
                                                        <span className="gbr-label gbr-b">{comparedUsername}</span>
                                                        <div className="gbr-track"><div className="gbr-fill gbr-fill-b" style={{ width: `${completionB}%` }} /></div>
                                                        <span className="gbr-count">{group.bAnimeCount}/{total}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Group detail overlay */}
            {groupDetailData && (
                <div className="compare-overlay-backdrop" onClick={() => setGroupDetailData(null)}>
                    <div className="compare-overlay compare-overlay-wide" onClick={e => e.stopPropagation()}>
                        <div className="compare-overlay-header">
                            <h3>📦 {groupDetailData.name}</h3>
                            <button className="compare-overlay-close" onClick={() => setGroupDetailData(null)}>✕</button>
                        </div>
                        <div className="group-detail-content">
                            <div className="group-detail-summary">
                                <span className="gds-common">🤝 {groupDetailData.commonCount || 0} shared</span>
                                <span className="gds-a">🔵 {groupDetailData.onlyAAnime?.length || 0} only {username}</span>
                                <span className="gds-b">🟡 {groupDetailData.onlyBAnime?.length || 0} only {comparedUsername}</span>
                            </div>
                            {groupDetailData.commonAnime?.length > 0 && (
                                <div className="group-detail-section">
                                    <h4 className="gds-title gds-title-common">🤝 Both Have ({groupDetailData.commonAnime.length})</h4>
                                    <div className="compare-anime-grid">
                                        {groupDetailData.commonAnime.map((manga, i) => (
                                            <MangaCard key={manga.media?.id || i} manga={manga} userAName={username} userBName={comparedUsername} showScores={true} onClick={() => setDetailManga(manga)} onAdd={(status) => handleAddToList(manga.media?.id, status)} isAdding={addingManga.has(manga.media?.id)} userMangaMap={userMangaMap} onRead={() => { setSelectedMangaForRead(getTitle(manga.media, titlePreference)); setShowReadModal(true); }} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {groupDetailData.onlyAAnime?.length > 0 && (
                                <div className="group-detail-section">
                                    <h4 className="gds-title gds-title-a">🔵 Only {username} Has ({groupDetailData.onlyAAnime.length})</h4>
                                    <div className="compare-anime-grid">
                                        {groupDetailData.onlyAAnime.map((manga, i) => (
                                            <MangaCard key={manga.media?.id || i} manga={manga} userAName={username} userBName={comparedUsername} showScores={false} onClick={() => setDetailManga(manga)} onAdd={(status) => handleAddToList(manga.media?.id, status)} isAdding={addingManga.has(manga.media?.id)} userMangaMap={userMangaMap} onRead={() => { setSelectedMangaForRead(getTitle(manga.media, titlePreference)); setShowReadModal(true); }} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {groupDetailData.onlyBAnime?.length > 0 && (
                                <div className="group-detail-section">
                                    <h4 className="gds-title gds-title-b">🟡 Only {comparedUsername} Has ({groupDetailData.onlyBAnime.length})</h4>
                                    <div className="compare-anime-grid">
                                        {groupDetailData.onlyBAnime.map((manga, i) => (
                                            <MangaCard key={manga.media?.id || i} manga={manga} userAName={username} userBName={comparedUsername} showScores={false} onClick={() => setDetailManga(manga)} onAdd={(status) => handleAddToList(manga.media?.id, status)} isAdding={addingManga.has(manga.media?.id)} userMangaMap={userMangaMap} onRead={() => { setSelectedMangaForRead(getTitle(manga.media, titlePreference)); setShowReadModal(true); }} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Saved comparisons overlay */}
            {showSavedOverlay && (
                <div className="compare-overlay-backdrop" onClick={() => setShowSavedOverlay(false)}>
                    <div className="compare-overlay compare-overlay-narrow" onClick={e => e.stopPropagation()}>
                        <div className="compare-overlay-header">
                            <h3>All Saved Comparisons ({savedMangaComparisons.length})</h3>
                            <button className="compare-overlay-close" onClick={() => setShowSavedOverlay(false)}>✕</button>
                        </div>
                        <div className="saved-overlay-list">
                            {savedMangaComparisons.map(saved => (
                                <div
                                    key={saved.userBUsername}
                                    className={`saved-item ${comparedUsername === saved.userBUsername ? 'active' : ''}`}
                                    onClick={() => { handleLoadSaved(saved.userBUsername); setShowSavedOverlay(false); }}
                                >
                                    <div className="saved-avatar">
                                        {saved.userBAvatar ? (
                                            <img src={saved.userBAvatar} alt="" />
                                        ) : (
                                            <div className="saved-avatar-placeholder">{saved.userBUsername[0]?.toUpperCase()}</div>
                                        )}
                                    </div>
                                    <div className="saved-info">
                                        <div className="saved-name">{saved.userBUsername}</div>
                                        <div className="saved-meta">
                                            {saved.summary?.compatScore}% compat · {saved.summary?.commonCount} common
                                        </div>
                                    </div>
                                    <button className="saved-delete" onClick={(e) => { e.stopPropagation(); handleDeleteSaved(saved.userBUsername, e); }} title="Delete">✕</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Detail modal — rendered LAST to appear above overlays */}
            {detailManga && (
                <MangaDetailModal
                    manga={detailManga}
                    onClose={() => setDetailManga(null)}
                    userAName={username}
                    userBName={comparedUsername}
                    onAdd={(status) => handleAddToList(detailManga.media?.id, status)}
                    isAdding={addingManga.has(detailManga.media?.id)}
                    userStatus={userMangaMap.get(detailManga.media?.id)}
                    onRead={() => { setSelectedMangaForRead(getTitle(detailManga.media, titlePreference)); setShowReadModal(true); }}
                />
            )}

            {/* Read Links Modal */}
            <SiteLinksModal
                isOpen={showReadModal}
                onClose={() => { setShowReadModal(false); setSelectedMangaForRead(null); }}
                links={readLinks}
                animeName={selectedMangaForRead || ''}
                type="read"
            />
        </div>
    );
};

export default MangaCompare;
